/**
 * SpendCard — Settings → Flint AI ▸ Spend & limits.
 *
 * Shows this month's AI credit usage (live, from the credit ledger) and the
 * recent ledger entries. The rupee daily-spend meter + editable cap are V1.x
 * (they need the provider cost gateway + cap enforcement) — shown
 * disabled-with-reason so the operator sees what's coming.
 */
import { IndianRupee, Sparkles } from "lucide-react";
import { Alert, StatusPill, cn } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { FormCard } from "../form-card.tsx";

export function SpendCard() {
  const balance = trpc.aiCredits.balance.useQuery();
  const recent = trpc.aiCredits.transactions.useQuery({ limit: 5, cursor: null });

  const allocation = balance.data?.monthlyAllocation ?? 0;
  const remaining = balance.data?.balance ?? 0;
  const used = Math.max(0, allocation - remaining);
  const pct = allocation > 0 ? Math.min(100, Math.round((used / allocation) * 100)) : 0;
  const tone = pct >= 90 ? "bad" : pct >= 70 ? "warn" : "ok";

  return (
    <FormCard
      id="spend"
      icon={<IndianRupee size={14} strokeWidth={2} aria-hidden />}
      title="Spend & limits"
      description="What Flint AI is using this month, and (soon) your daily ceiling in rupees."
      badge={
        balance.data ? (
          <StatusPill tone="sky" size="xs">
            {balance.data.plan} plan
          </StatusPill>
        ) : undefined
      }
    >
      {balance.isError ? (
        <Alert intent="bad" title="Couldn't load usage" description={balance.error.message} />
      ) : (
        <>
          {/* Credit usage — live */}
          <div className="rounded-md border border-line bg-paper p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-ink">AI credits this month</p>
              <p className="font-mono text-sm tabular-nums text-ink-2">
                {used.toLocaleString("en-IN")} / {allocation.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  tone === "bad" ? "bg-bad" : tone === "warn" ? "bg-warn" : "bg-ok",
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted">
              {remaining.toLocaleString("en-IN")} credits left · chat 2 · briefing 5 · bill scan 15
              each. Resets monthly.
            </p>
          </div>

          {/* Daily ₹ cap — V1.x disabled-with-reason */}
          <div className="mt-3 rounded-md border border-dashed border-line bg-bg/40 p-4 opacity-80">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-muted" aria-hidden />
              <p className="text-sm font-medium text-ink-2">Daily spend cap (₹)</p>
              <StatusPill tone="muted" size="xs">
                Coming soon
              </StatusPill>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-muted">
              Set a daily rupee ceiling with alerts at 80% and 100%; past the cap, Flint falls back
              to non-AI paths instead of spending more. Arrives with per-call cost tracking.
            </p>
          </div>

          {/* Recent activity — live */}
          {recent.data && recent.data.rows.length > 0 ? (
            <div className="mt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Recent usage
              </p>
              <div className="divide-y divide-line rounded-md border border-line bg-paper">
                {recent.data.rows.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">{r.description}</p>
                      <p className="text-xs text-muted">{formatDate(r.createdAt)}</p>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 font-mono text-xs tabular-nums",
                        r.credits < 0 ? "text-ink-2" : "text-ok",
                      )}
                    >
                      {r.credits > 0 ? "+" : ""}
                      {r.credits} cr
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </FormCard>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
